import React, { useState, useEffect } from 'react';
import {
    Box,
    Center,
    Spinner,
    Table,
    Tbody,
    Thead,
    Tr,
    Td,
    Th,
    VStack,
    Text,
    InputGroup,
    InputLeftElement,
    Input,
    Select,
    HStack,
    ButtonGroup,
    Button,
    Icon,
    Stack,
} from '@chakra-ui/react';
import { FiSearch, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import useHttp from '../utils/http';

const ListPrintedTags = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasBeenUsedFilter, setHasBeenUsedFilter] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
    const { get, patch } = useHttp();

    const getData = async () => {
        try {
            const username = process.env.REACT_APP_USER;
            const password = process.env.REACT_APP_PASSWORD;
            const { data } = await get('/api/print', { timeout: 80000 }, { username, password });
            setData(data);
            setFilteredData(data);
            setIsLoading(false);
        } catch (error) {
            console.log(error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const lowercasedQuery = searchQuery.toLowerCase();
        const filtered = data.filter(item => {
            const matchesQuery = (item.sap && item.sap.toLowerCase().includes(lowercasedQuery)) ||
                (item.serial && item.serial.toLowerCase().includes(lowercasedQuery)) ||
                (item.epc && item.epc.toLowerCase().includes(lowercasedQuery));
            const matchesFilter =
                hasBeenUsedFilter === 'all' ||
                (hasBeenUsedFilter === 'true' && item.hasBeenUsed) ||
                (hasBeenUsedFilter === 'false' && !item.hasBeenUsed);

            return matchesQuery && matchesFilter;
        });

        const sorted = [...filtered].sort((a, b) => {
            if (sortConfig.direction === 'asc') {
                return a[sortConfig.key] > b[sortConfig.key] ? 1 : -1;
            } else {
                return a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
            }
        });

        setFilteredData(sorted);
        setCurrentPage(1);
    }, [searchQuery, hasBeenUsedFilter, data, sortConfig]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortArrow = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'asc' ? <FiArrowUp /> : <FiArrowDown />;
        }
        return null;
    };

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
            .toString()
            .padStart(2, '0')}/${date.getFullYear()}`;
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes()
            .toString()
            .padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const indexOfLastItem = rowsPerPage === 'all' ? filteredData.length : currentPage * rowsPerPage;
    const indexOfFirstItem = rowsPerPage === 'all' ? 0 : indexOfLastItem - rowsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

    const totalPages = rowsPerPage === 'all' ? 1 : Math.ceil(filteredData.length / rowsPerPage);

    const handleLeidoClick = async (item) => {
        const username = process.env.REACT_APP_USER;
        const password = process.env.REACT_APP_PASSWORD;
        const newState = !item.hasBeenUsed;
        if (window.confirm(`Seguro que quieres cambiar a ${newState ? 'Sí' : 'No'}`)) {
            //console.log(`Cambiando a ${newState ? 'Sí' : 'No'}`);
            try {
                await patch(`/api/print/label/${item.id}`, { hasBeenUsed: newState }, { username, password });
                await getData();
            } catch (error) {
                console.log(error);
            }


        }
    };


    if (isLoading) {
        return (
            <Center height="100vh" width="100%">
                <Spinner size="xl" thickness="6px" speed="0.65s" emptyColor="gray.200" color="blue.500" width="150px" height="150px" />
            </Center>
        )
    }

    return (
        <Box w="full" py={{ base: '4', md: '8' }} px={{ base: '0', md: 8 }}>
            <Box
                bg="bg.surface"
                boxShadow={{ base: 'none', md: 'sm' }}
                borderRadius={{ base: 'none', md: 'lg' }}
                w="full"
            >
                <Stack direction="row" justify="flex-end" px={{ base: '4', md: '6' }} pt="5">
                    <InputGroup maxW="xs">
                        <InputLeftElement pointerEvents="none">
                            <Icon as={FiSearch} color="fg.muted" boxSize="5" />
                        </InputLeftElement>
                        <Input
                            placeholder="Buscar"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </InputGroup>
                </Stack>
                <Stack direction="row" justify="flex-end" px={{ base: '4', md: '6' }} pt="4" mb="10">
                    <Select
                        maxW="200px"
                        value={hasBeenUsedFilter}
                        onChange={(e) => setHasBeenUsedFilter(e.target.value)}
                    >
                        <option value="all">Todos</option>
                        <option value="true">Leído</option>
                        <option value="false">No Leído</option>
                    </Select>
                </Stack>
                <Box mt="10" mb="10" overflowX="auto" overflowY="auto" maxHeight="500px" w="full">
                    <Table w="full">
                        <Thead>
                            <Tr>
                                <Th onClick={() => handleSort('sap')}>
                                    SAP {getSortArrow('sap')}
                                </Th>
                                <Th onClick={() => handleSort('serial')}>
                                    Serial {getSortArrow('serial')}
                                </Th>
                                <Th onClick={() => handleSort('epc')}>
                                    EPC {getSortArrow('epc')}
                                </Th>
                                <Th onClick={() => handleSort('createdAt')}>
                                    Fecha {getSortArrow('createdAt')}
                                </Th>
                                <Th onClick={() => handleSort('hasBeenUsed')}>
                                    Leido {getSortArrow('hasBeenUsed')}
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {currentItems.map((item) => (
                                <Tr key={item.id}>
                                    <Td>
                                        <Box fontWeight="bold">{item.sap}</Box>
                                    </Td>
                                    <Td>
                                        <Box>{item.serial || ''}</Box>
                                    </Td>
                                    <Td>
                                        <Box>{item.epc}</Box>
                                    </Td>
                                    <Td>
                                        <VStack align="start" spacing={0}>
                                            <Text>{formatDate(item.createdAt)}</Text>
                                            <Text color="gray.500" fontSize="sm">
                                                {formatTime(item.createdAt)}
                                            </Text>
                                        </VStack>

                                    </Td>
                                    <Td>
                                        <Box cursor="pointer" onClick={() => handleLeidoClick(item)}>{item.hasBeenUsed ? 'Yes' : 'No'}</Box>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
                <Box px={{ base: '4', md: '6' }} pt="4" pb="8" w="full">
                    <HStack spacing="3" justify="space-between">
                        <Text color="gray.600">
                            Mostrando {indexOfFirstItem + 1} de {indexOfLastItem} de {filteredData.length} resultados
                        </Text>
                        <HStack spacing="3">
                            <Select
                                maxW="100px"
                                value={rowsPerPage}
                                onChange={(e) => setRowsPerPage(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value="all">All</option>
                            </Select>
                            <ButtonGroup
                                spacing="3"
                                justifyContent="space-between"
                                width={{ base: 'full', md: 'auto' }}
                                variant="secondary"
                            >
                                <Button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    isDisabled={currentPage === 1 || rowsPerPage === 'all'}
                                >
                                    Anterior
                                </Button>
                                <Button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    isDisabled={currentPage === totalPages || rowsPerPage === 'all'}
                                >
                                    Siguiente
                                </Button>
                            </ButtonGroup>
                        </HStack>
                    </HStack>
                </Box>
            </Box>
        </Box>
    );
};

export default ListPrintedTags;
